// src/components/HeaderUser.jsx
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaShoppingBasket,
  FaShoppingCart,
  FaMapMarkerAlt,
  FaUser,
  FaSignOutAlt,
  FaHeart,
} from "react-icons/fa";
import "../css/HeaderUser.css";
import { useState, useEffect } from "react";
import CustomerCartDrawer from "./CustomerCartDrawer"; // drawer for logged-in cart
import CustomerWishlistDrawer from "./CustomerWishlistDrawer";

const HeaderUser = () => {
  const navigate = useNavigate();

  // open/close states
  const [showWishlist, setShowWishlist] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);

  // counts
  const [cartCount, setCartCount] = useState(() => {
    try {
      const raw =
        sessionStorage.getItem("cart") ?? localStorage.getItem("cart");
      return raw ? JSON.parse(raw).length : 0;
    } catch {
      return 0;
    }
  });

  const [wishlistCount, setWishlistCount] = useState(() => {
    try {
      const raw =
        sessionStorage.getItem("wishlist") ?? localStorage.getItem("wishlist");
      return raw ? JSON.parse(raw).length : 0;
    } catch {
      return 0;
    }
  });

  // keep counts in sync with storage changes from other tabs/components
  useEffect(() => {
    const onStorage = (e) => {
      if (!e || !e.key) {
        // fallback: recompute both
        refreshLocalCounts();
        return;
      }
      if (e.key === "cart") {
        try {
          const raw =
            sessionStorage.getItem("cart") ?? localStorage.getItem("cart");
          setCartCount(raw ? JSON.parse(raw).length : 0);
        } catch {
          setCartCount(0);
        }
      }
      if (e.key === "wishlist") {
        try {
          const raw =
            sessionStorage.getItem("wishlist") ??
            localStorage.getItem("wishlist");
          setWishlistCount(raw ? JSON.parse(raw).length : 0);
        } catch {
          setWishlistCount(0);
        }
      }
    };

    const refreshLocalCounts = () => {
      try {
        const rawCart =
          sessionStorage.getItem("cart") ?? localStorage.getItem("cart");
        setCartCount(rawCart ? JSON.parse(rawCart).length : 0);
      } catch {
        setCartCount(0);
      }
      try {
        const rawWish =
          sessionStorage.getItem("wishlist") ??
          localStorage.getItem("wishlist");
        setWishlistCount(rawWish ? JSON.parse(rawWish).length : 0);
      } catch {
        setWishlistCount(0);
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const userLogout = () => {
    toast.success("Logged out!", {
      position: "top-center",
      autoClose: 1000,
    });
    sessionStorage.removeItem("active-user");
    sessionStorage.removeItem("customer-jwtToken");
    setTimeout(() => {
      window.location.reload(true);
      navigate("/home");
    }, 1200);
  };

  return (
    <>
      <ul className="navbar-nav ms-auto mb-2 mb-lg-0 me-4 header-user-icon-menu">
        {/* Wishlist (opens drawer) */}
        <li className="nav-item position-relative">
          <button
            type="button"
            onClick={() => setShowWishlist(true)}
            className="icon-only-link"
            aria-label="My Wishlist"
          >
            <div className="icon-fixed-size">
              <FaHeart />
            </div>
            {wishlistCount > 0 && (
              <span className="wishlist-only-badge" aria-hidden>
                {wishlistCount}
              </span>
            )}
            <div className="icon-tooltip">My Wishlist</div>
          </button>
        </li>

        {/* Cart (opens drawer for logged-in user) */}
        <li className="nav-item position-relative">
          <button
            className="icon-only-link"
            aria-label="My Cart"
            onClick={() => setShowCartModal(true)}
            type="button"
          >
            <div className="icon-fixed-size">
              <FaShoppingBasket />
            </div>
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            <div className="icon-tooltip">My Cart</div>
          </button>
        </li>

        {/* Orders (use shopping cart icon) */}
        <li className="nav-item position-relative">
          <Link
            to="/user/myorder"
            className="icon-only-link"
            aria-label="My Orders"
          >
            <div className="icon-fixed-size">
              <FaShoppingCart />
            </div>
            <div className="icon-tooltip">My Orders</div>
          </Link>
        </li>

        {/* Address Dropdown */}
        <li className="nav-item dropdown position-relative">
          <a
            className="icon-only-link dropdown-toggle"
            href="#"
            id="addressDropdown"
            role="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <div className="icon-fixed-size">
              <FaMapMarkerAlt />
            </div>
          </a>
          <ul className="dropdown-menu" aria-labelledby="addressDropdown">
            <li>
              <Link to="/delivery/address/add" className="dropdown-item">
                Add Address
              </Link>
            </li>
            <li>
              <Link to="/delivery/address/view" className="dropdown-item">
                View Address
              </Link>
            </li>
          </ul>
          <div className="icon-tooltip">Addresses</div>
        </li>

        {/* Profile */}
        <li className="nav-item position-relative">
          <Link
            to="/user/profile"
            className="icon-only-link"
            aria-label="Profile"
          >
            <div className="icon-fixed-size">
              <FaUser />
            </div>
            <div className="icon-tooltip">My Profile</div>
          </Link>
        </li>

        {/* Logout */}
        <li className="nav-item position-relative">
          <button
            onClick={userLogout}
            className="icon-only-link"
            aria-label="Logout"
          >
            <div className="icon-fixed-size">
              <FaSignOutAlt />
            </div>
            <div className="icon-tooltip">Logout</div>
          </button>
          <ToastContainer />
        </li>
      </ul>

      {/* Customer cart drawer (logged-in) */}
      <CustomerCartDrawer
        show={showCartModal}
        onClose={() => setShowCartModal(false)}
        onCartChange={(count) => setCartCount(count)}
      />

      {/* Customer wishlist drawer (logged-in) */}
      <CustomerWishlistDrawer
        show={showWishlist}
        onClose={() => setShowWishlist(false)}
        onWishlistChange={(newCount) => setWishlistCount(newCount)}
      />
    </>
  );
};

export default HeaderUser;
